import React, { FC, useMemo, useRef, useState } from 'react';
import { Button, Center, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { UserPill } from 'components/core/UserPill';
import { DataTable, ServerSort } from 'components/modules/DataTable';
import { QALogViewProps } from './QALog.props';
import { BsClockHistory } from 'react-icons/bs';
import { AddQALogModal } from 'components/modules/AddQALogModal';
import { RefType } from 'components/modules/AddQALogModal/AddQALogModal.props';
import { EditRefType } from 'components/modules/EditQALogModal/EditQALogModal.props';
import EditQALogModal from 'components/modules/EditQALogModal/EditQALogModal.container';
import { washHub } from 'store';
import { Filters } from 'components/primitives/Filters';
import { useMutation } from 'react-query';
import { exportQALogCsv } from 'api/QALog';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import { washhubSelector } from 'store/slices/washhub';
import { useAppSelector } from 'store/hooks';
import { QALog } from 'types/QALog';

import { buildQaLogColumns } from './qaLogColumns';

const QALogView: FC<QALogViewProps> = (props) => {
  const {
    qaLogs,
    isLoading,
    pagination,
    startDate,
    endDate,
    onClearFilters,
    onApplyFilters,
    onSearchClear,
    onSearchPress,
    employees,
    roles,
    setSortBy,
  } = props;
  const addQALogRef = useRef<RefType>(null);
  const editQALogModalRef = useRef<EditRefType>(null);
  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const csvRef = useRef<any>(null);
  const washHubs = useAppSelector(washhubSelector);
  const [sort, setSort] = useState<ServerSort | undefined>(undefined);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    async () => {
      let response: string = '';

      response = await exportQALogCsv({
        washHub: washHubId,
        startDate,
        endDate,
        employees,
        roles,
      });

      return response;
    },
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.click();
        }, 500);
      },
    },
  );

  const renderExpBtn = () => {
    return (
      <>
        <Button
          isLoading={isExporting}
          ml={2}
          minW="200px"
          onClick={() => {
            mutate();
          }}>
          Export QA Log to CSV
        </Button>
        {Boolean(csvData) && (
          <a
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(csvData)}`}
            download={`washhub-${selectedWashHub?.name}-qa-log.csv`}
            style={{ display: 'none' }}
            ref={csvRef}>
            Export CSV
          </a>
        )}
      </>
    );
  };

  const columns = useMemo(() => buildQaLogColumns(washHubId, washHubs), [
    washHubId,
    washHubs,
  ]);

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Flex align="flex-start" justify="space-between">
        <Heading mb={6} fontSize="24px">
          QA Log
        </Heading>
        <Flex align="flex-end" gap={4}>
          {renderExpBtn()}
          <Button
            minW="200px"
            leftIcon={<BsClockHistory size="20px" />}
            isDisabled={washHubId === ''}
            onClick={() => {
              addQALogRef?.current?.onOpen?.();
            }}>
            Create QA Log
          </Button>
          <Stack isInline>
            <UserPill />
          </Stack>
        </Flex>
      </Flex>
      <Flex mt="20px" alignItems="flex-end">
        <DateRangeInputExt
          startDate={startDate}
          endDate={endDate}
          onSearchClear={onSearchClear}
          onSearchPress={onSearchPress}
        />
      </Flex>
      <Flex mt="20px" justify="space-between">
        <Filters
          isRolesOnly
          isEmployeesOnly
          isFullButton
          employees={employees}
          roles={roles}
          onApplyFilters={onApplyFilters}
          onClearFilters={onClearFilters}
        />
      </Flex>
      <DataTable<QALog>
        mt={0}
        minW="1300px"
        data={qaLogs}
        columns={columns}
        isLoading={isLoading}
        pagination={pagination}
        sort={sort}
        onSortChange={(nextSort) => {
          setSort(nextSort);
          setSortBy(nextSort.dir);
        }}
        onRowClick={(qaLog) => {
          editQALogModalRef?.current?.onOpen?.(qaLog);
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No QA Log available</Text>
          </Center>
        )}
      />
      <AddQALogModal ref={addQALogRef} />
      <EditQALogModal ref={editQALogModalRef} />
    </Flex>
  );
};

export default QALogView;
